import { useEffect, useState } from 'react';
import { Button, Card, CardBody, Input, UncontrolledTooltip } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiAccountOutline, mdiInformationOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from '@tanstack/react-query';
import BonusSummary from './components/BonusSummary';
import BonusDetails from './components/BonusDetails';
import { ManagerAutocompleteSelect } from '../../components/Common';
import './Bonus.scss';
import { ExportUtil } from '../../utils';
import {
  fetchBonusDetails,
  fetchPCMRating,
} from '../../store/apis/bonusService';
import { toFixedTwoDecimal } from '../../utils/number.util';
import download from '../../assets/images/bonus/download.png';
import pcmRating from '../../assets/images/bonus/pcmRating.png';
import yearly from '../../assets/images/bonus/yearly.png';
import { fetchEmployeeDetails } from '../../store/apis/employeeService';
import { fetchHRCodeList } from '../../store/apis/commonService';

function Bonus() {
  document.title = 'Bonus Overview | Envision Daikin';
  const currentYear = new Date().getFullYear();
  const [pcmPeriod, setPcmPeriod] = useState(
    `${currentYear}-${currentYear + 1}`,
  );

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [callAPI, setCallAPI] = useState(false);
  const [formData, setFormData] = useState({
    empCode: selectedEmployee?.value || '',
    yr: currentYear,
    period: pcmPeriod,
  });

  const { data: currentFiscalYear } = useQuery({
    queryKey: ['currentFiscalYear'],
    queryFn: () => fetchHRCodeList({ type: 'FISCAL_YEAR' }),
    enabled: true,
    keepPreviousData: true,
  });

  const { data: user } = useQuery({
    queryKey: ['employeeDetails'],
    queryFn: async () => {
      const res = await fetchEmployeeDetails({
        type: 'PROFILE',
      });
      return res?.[0];
    },
    refetchOnWindowFocus: true,
    staleTime: 0, // Data is always stale, so refetches on mount
    cacheTime: 0,
  });

  useEffect(() => {
    if (user) {
      setSelectedEmployee({
        value: user?.EmployeeNo?.trim(),
        label: user?.EmployeeName?.trim(),
      });
      setCallAPI(true);
    }
  }, [user]);

  const { mutate: getPCMRating, data: pcmRatingData } = useMutation({
    mutationFn: fetchPCMRating,
    onError: error => {
      toast.error(error?.message || 'Failed to fetch PCM rating data.', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (selectedEmployee?.value && callAPI && formData?.period) {
      getPCMRating({
        empCode: selectedEmployee?.value,
        period: formData?.period,
      });

      setFormData(prev => ({
        ...prev,
        empCode: selectedEmployee.value,
        period: formData?.period,
      }));
    }
  }, [selectedEmployee, callAPI, getPCMRating, formData?.period]);

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    try {
      // Fetch data with proper error handling
      const summaryResponse = await fetchBonusDetails({
        type: 'SUMMARY',
        ...formData,
      });

      const detailsResponse = await fetchBonusDetails({
        type: 'DETAIL',
        ...formData,
      });

      // Validate API responses
      if (!summaryResponse || !detailsResponse) {
        toast.error('Failed to fetch export data', {
          className: 'toast-error',
        });
        return;
      }

      // Transform data with null checks
      const summaryData = summaryResponse.map(item => ({
        Qtr: item?.Qtr ? `Q${item.Qtr}` : '',
        Eligibility_Qtr: toFixedTwoDecimal(item?.Eligibility_Qtr ?? 0),
        Eligibility_QtrCumulative: toFixedTwoDecimal(
          item?.Eligibility_QtrCumulative ?? 0,
        ),
        Actual_QtrCumulative: toFixedTwoDecimal(
          item?.Actual_QtrCumulative ?? 0,
        ),
        TotalActual: toFixedTwoDecimal(item?.TotalActual ?? 0),
      }));

      const detailsData = detailsResponse.map(item => ({
        Qtr: item?.Qtr ? `Q${item.Qtr}` : '',
        Criteria: item?.KPI || '',
        Weight: item?.Weight ?? 0,
        Target: item?.Target ?? 0,
        Result: item?.Result ?? 0,
        Carry_Fwd: item?.CarryForward ?? 0,
        ResultPerc: item?.ResultPerc ? `${item.ResultPerc}%` : '0%',
        Score: item?.Score ?? 0,
        AtualPayout: toFixedTwoDecimal(item?.Bonus ?? 0),
        CumTarget: item?.Cumulative_Target ?? 0,
        CumResult: item?.Cumulative_Result ?? 0,
        CumResultPerc: item?.Cumulative_ResultPerc
          ? `${item.Cumulative_ResultPerc}%`
          : '0%',
        CumScore: item?.Cumulative_Score ?? 0,
        CumPayout: item?.Cumulative_Bonus ?? 0,
      }));

      // Check if we have data to export
      if (!summaryData.length && !detailsData.length) {
        toast.warning('No data available to export.', {
          className: 'toast-warning',
        });
        return;
      }

      // Create sheets array
      const sheetsData = [];
      // Add summary sheet if data exists
      if (summaryData.length) {
        // Calculate totals
        const totalPayout = summaryData.reduce(
          (sum, item) => sum + (parseFloat(item.Actual_QtrCumulative) || 0),
          0,
        );
        const actualPayout = summaryData.reduce(
          (sum, item) => sum + (parseFloat(item.TotalActual) || 0),
          0,
        );
        sheetsData.push({
          data: [
            ...summaryData,
            // Add total row
            {
              Qtr: '',
              Eligibility_Qtr: '',
              Eligibility_QtrCumulative: 'Quarterly Bonus Payout',
              Actual_QtrCumulative: toFixedTwoDecimal(totalPayout),
              TotalActual: toFixedTwoDecimal(actualPayout),
            },
          ],
          sheetName: 'Summary',
          columns: {
            Qtr: 'Quarter',
            Eligibility_Qtr: 'Qtly. Payout Multiplier',
            Eligibility_QtrCumulative: 'Cum. Payout Multiplier',
            Actual_QtrCumulative: 'Total Payout',
            TotalActual: 'Actual Payout',
          },
        });
      }

      // Add details sheet if data exists
      if (detailsData.length) {
        sheetsData.push({
          data: detailsData,
          sheetName: 'Details',
          columns: {
            Qtr: 'Quarter',
            Criteria: 'Criteria',
            Weight: 'Weight',
            Target: 'Target',
            Result: 'Result',
            Carry_Fwd: 'Carry Forward',
            ResultPerc: 'Result %',
            Score: 'Score',
            AtualPayout: 'Actual Payout',
            CumTarget: 'Cumulative Target',
            CumResult: 'Cumulative Result',
            CumResultPerc: 'Cumulative Result %',
            CumScore: 'Cumulative Score',
            CumPayout: 'Cumulative Payout',
          },
        });
      }

      // Call export utility
      await ExportUtil.downloadBonusExcel(sheetsData, {
        fileName: `Bonus_Report_${formData.empCode}_${formData.yr}.xlsx`,
      });
    } catch (error) {
      toast.error('Failed to export data', {
        className: 'toast-error',
      });
    }
  };

  const getMangerRatingOutput = mgrRating => {
    let output = 0;
    if (!mgrRating) return '-';
    const rating = parseFloat(mgrRating);
    if (rating >= 3) {
      output = 1;
    }
    return output;
  };

  const getDefaultActiveFiscalYear = () => {
    const foundCurrentYear = currentFiscalYear?.find(
      year => year?.IsActive === '1',
    );
    return foundCurrentYear?.Code ?? currentYear;
  };

  useEffect(() => {
    if (currentFiscalYear) {
      setFormData(prev => ({
        ...prev,
        yr: getDefaultActiveFiscalYear(),
        period: `${getDefaultActiveFiscalYear()}-${parseInt(getDefaultActiveFiscalYear(), 10) + 1}`,
      }));
      setPcmPeriod(
        `${getDefaultActiveFiscalYear()}-${parseInt(getDefaultActiveFiscalYear(), 10) + 1}`,
      );
    }
  }, [currentFiscalYear]);

  return (
    <div className="page-content px-0" style={{ marginTop: '-1.5rem' }}>
      {/* Header */}
      <div className="bonus-header px-3 pt-3 pb-2">
        <div className="d-flex flex-row align-items-center justify-content-between">
          <div className="fw-semibold fs-5">Bonus Overview</div>
          <div className="d-flex flex-row align-items-center gap-3">
            {/* Employee */}
            <div className="d-flex flex-row align-items-center gap-2">
              <b>Employee</b>
              <ManagerAutocompleteSelect
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.value,
                    label: item.label,
                    name: item.name,
                    code: item.code,
                    designation: item.designation,
                  }))
                }
                value={selectedEmployee}
                onChange={option => setSelectedEmployee(option)}
                isClearable={false}
                displayOption="bothCustom"
                inputWidth="520px"
                width="100%"
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                rightKey="value"
                defaultShowMenu
              />
            </div>
            {/* Fiscal Year */}
            <div className="d-flex flex-row align-items-center gap-2">
              <b className="text-nowrap">Fiscal Year</b>
              <Input
                type="select"
                className="form-select external-filter-input"
                style={{ fontWeight: 600 }}
                defaultValue={getDefaultActiveFiscalYear()}
                value={formData?.yr}
                onChange={e => {
                  setFormData(prev => ({
                    ...prev,
                    yr: e.target.value,
                    period: `${e.target.value}-${parseInt(e.target.value, 10) + 1}`,
                  }));
                  setPcmPeriod(
                    `${e.target.value}-${parseInt(e.target.value, 10) + 1}`,
                  );
                }}
              >
                {currentFiscalYear
                  ?.sort((a, b) => Number(a?.SortOrder) - Number(b?.SortOrder))
                  .map(year => (
                    <option key={year?.Code} value={year?.Code}>
                      {year?.Description}
                    </option>
                  ))}
              </Input>
            </div>
            <div>
              <Button className="custom-light-btn" onClick={handleExportExcel}>
                <img src={download} alt="download" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex flex-column gap-2 px-3 py-2">
        {/* Bonus Summary */}
        <BonusSummary formData={formData} callAPI={callAPI} />

        {/* Bonus Details */}
        <BonusDetails formData={formData} callAPI={callAPI} />
      </div>
      {/* Yearly */}
      <div className="yearly-section px-3">
        <div className="fw-semibold mb-1 mt-2">
          <div className="yearly-header mb-1">
            <img src={yearly} alt="yearly" />
            <div className="fs-6 fw-semibold">Yearly</div>
          </div>
        </div>
        <Card className="quarterly-card">
          <CardBody>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <div
                className="d-flex flex-row align-items-center gap-3"
                style={{ width: '50%' }}
              >
                <div className="pcm-rating-title">
                  <img height="20px" src={pcmRating} alt="pcm-rating" />
                  <div className="pcm-rating-text">PCM Rating</div>
                  <div>
                    <span id="pcm-rating-info">
                      <Icon
                        path={mdiInformationOutline}
                        size={0.8}
                        style={{ cursor: 'pointer' }}
                      />
                    </span>
                    <UncontrolledTooltip
                      placement="top"
                      target="pcm-rating-info"
                    >
                      PCM Rating - Performance and Competency Management rating
                      that determines yearly bonus eligibility. Higher ratings
                      result in higher bonus payouts.
                    </UncontrolledTooltip>
                  </div>
                </div>
                <div className="pcm-rating-wrapper">
                  <div
                    className="pcm-rating-actual"
                    title="Actual  no. of months basic salary payout as per the final PCM rating"
                  >
                    Actual
                  </div>
                  <div className="pcm-rating-value">
                    {pcmRatingData?.data?.[0]?.Avg_Rating_PCM ?? '-'}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-row align-items-center gap-3">
                <div className="pcm-rating-title">
                  <div className="pcm-rating-text">PCM Bonus Score</div>
                  <div>
                    <span id="m-rating-info">
                      <Icon
                        path={mdiInformationOutline}
                        size={0.8}
                        style={{ cursor: 'pointer' }}
                      />
                    </span>
                    <UncontrolledTooltip placement="top" target="m-rating-info">
                      If PCM Rating is 0,1,2 then PCM Bonus Score is 0. If PCM
                      Rating is 3,4,5 then PCM Bonus Score is 1
                    </UncontrolledTooltip>
                  </div>
                </div>
                <div className="pcm-rating-wrapper">
                  <div className="pcm-bonus-value">
                    {getMangerRatingOutput(
                      pcmRatingData?.data?.[0]?.Avg_Rating_PCM,
                    ) ?? '-'}
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default Bonus;
